import React, { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "../../../lib/utils";
import { getProfileById } from "../api/staff";
import { buildProfileUpdateBody, getNotificationPrefs } from "../api/transformers";
import { useGetProfiles, useUpdateProfileMutation } from "../api/react-query";
import { useGetJobs, useGetTargetGroups, useGetSaleoffices } from "../../reference/api/react-query";
import { formatRelativeTimeFa } from "../../../shared/utils/relativeTime";
import { formatDateTime, localizeDigits } from "../../../shared/utils/numberLocale";
import { fmtInt } from "../../../shared/utils/formatters";
import {
  Button,
  Input,
  Select,
  Badge,
  AppDialog,
  DialogFooter,
  FormField,
  LoadingBlock,
  DataTableCard,
  DataTableRow,
  DataTableCell,
  Pagination,
  SearchInput,
  EmptyState,
} from "../../../shared/ui/composites";
import StaffProductGroupsTab from "./StaffProductGroupsTab";
import StaffMonthlyCommissionTab from "./StaffMonthlyCommissionTab";
import {
  emptyGroupPercents,
  readGroupCommissionMap,
  readMonthlyCommission,
  sumNumbers,
  almostEqual,
} from "../utils/commissionConfig";

const STAFF_COLUMNS = [
  { key: "index", label: "#" },
  { key: "code", label: "کد" },
  { key: "name", label: "نام" },
  { key: "national", label: "کد ملی" },
  { key: "job", label: "شغل" },
  { key: "supervisor", label: "سرپرست" },
  { key: "saleoffice", label: "خط/فروش" },
  { key: "dc", label: "مرکز" },
  { key: "notif_email", label: "ایمیل" },
  { key: "notif_sms", label: "پیامک" },
  { key: "notif_active", label: "ورود" },
  { key: "notif_targetting", label: "تارگت" },
  { key: "notif_targetting_cal", label: "تقویم تارگت" },
  { key: "last_login", label: "آخرین ورود" },
  { key: "actions", label: "تنظیمات" },
];

function LastLoginCell({ value }) {
  const label = formatRelativeTimeFa(value);
  if (!label) {
    return (
      <span className="inline-flex items-center rounded-md bg-slate-50 px-1.5 py-0.5 text-xs font-medium text-slate-400">
        —
      </span>
    );
  }

  const title = value ? formatDateTime(value) : "";
  const isFresh = label === "همین الان" || label.includes("دقیقه");

  return (
    <span
      title={title}
      className={cn(
        "inline-flex items-center gap-1 whitespace-nowrap rounded-md px-1.5 py-0.5 text-xs font-semibold leading-tight",
        isFresh
          ? "bg-emerald-50 text-emerald-700"
          : label.includes("ساعت")
            ? "bg-sky-50 text-sky-700"
            : "bg-slate-100 text-slate-600",
      )}
    >
      <span aria-hidden="true" className="text-xs opacity-70">
        ◷
      </span>
      {label}
    </span>
  );
}

function NotifToggle({ active, disabled, title, onToggle }) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onToggle}
      className={cn(
        "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition-colors",
        active
          ? "border-emerald-400 bg-emerald-500"
          : "border-slate-200 bg-slate-200",
        disabled && "cursor-not-allowed opacity-50",
      )}
      aria-pressed={active}
    >
      <span
        className={cn(
          "inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform",
          active ? "-translate-x-6" : "-translate-x-1",
        )}
      />
    </button>
  );
}

const MODAL_TABS = [
  { key: "role", label: "نقش", icon: "👤" },
  { key: "target", label: "هدف ماهانه", icon: "🎯" },
  { key: "products", label: "گروه کالا", icon: "📦" },
  { key: "saleoffices", label: "دفاتر فروش", icon: "🏢" },
  { key: "commission", label: "پورسانت ماهانه", icon: "💰" },
];

const SUP_PAGE_SIZE = 8;

export default function StaffTab() {
  const { data: staff = [], isLoading: loading, refetch: refetchStaff } = useGetProfiles();
  const { data: jobs = [] } = useGetJobs();
  const { data: targetGroups = [] } = useGetTargetGroups();
  const { data: saleoffices = [] } = useGetSaleoffices();
  const updateProfile = useUpdateProfileMutation();

  const [submitting, setSubmitting] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDcName, setSelectedDcName] = useState("");
  const [selectedSupervisorId, setSelectedSupervisorId] = useState("");
  const [supervisorQuery, setSupervisorQuery] = useState("");
  const [supervisorDropdownOpen, setSupervisorDropdownOpen] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState("");
  const [hasSupervisor, setHasSupervisor] = useState(""); // "" = all, "yes" = with supervisor, "no" = no supervisor
  const [emailSub, setEmailSub] = useState(""); // "" | "yes" | "no"
  const [smsSub, setSmsSub] = useState(""); // "" | "yes" | "no"
  const [activeSub, setActiveSub] = useState(""); // "" | "yes" | "no"
  const [targettingSub, setTargettingSub] = useState(""); // "" | "yes" | "no"
  const [targettingJalaliSub, setTargettingJalaliSub] = useState("");
  const [targettingGregorianSub, setTargettingGregorianSub] = useState("");

  const [selectedStaff, setSelectedStaff] = useState(null);

  const [role, setRole] = useState("");
  const [supervisorId, setSupervisorId] = useState(null);
  const [financialClaims, setFinancialClaims] = useState("");
  const [selectedSaleoffices, setSelectedSaleoffices] = useState([]);

  const [selectedTargetGroups, setSelectedTargetGroups] = useState([]);
  const [groupPercents, setGroupPercents] = useState({});
  const [monthlyCommission, setMonthlyCommission] = useState(readMonthlyCommission(null));

  const [ruleActiveTab, setRuleActiveTab] = useState("role");

  // Supervisor search state (used in the role tab)
  const [supSearch, setSupSearch] = useState("");
  const [supDcFilter, setSupDcFilter] = useState("");
  const [supPage, setSupPage] = useState(1);

  const [tablePage, setTablePage] = useState(1);

  const supervisorDropdownRef = useRef(null);

  const TABLE_PAGE_SIZE = 15;

  const getJobName = (id) => {
    const found = jobs.find((j) => String(j.id) === String(id));
    return found ? found.name : "";
  };

  const staffMap = staff.reduce((m, s) => {
    m[s.personnel_id] = s;
    return m;
  }, {});

  const dcOptions = [...new Set(staff.map((s) => s.dc_name))].filter(Boolean);

  // =========================
  // FILTERS (main table)
  // =========================

  const filteredStaff = staff.filter((s) => {
    const q = searchTerm.toLowerCase();
    const matchSearch =
      s.full_name?.toLowerCase().includes(q) ||
      String(s.personnel_code || "").includes(q) ||
      String(s.national_code || "").includes(q);
    const matchDc = !selectedDcName || s.dc_name === selectedDcName;
    const matchSupervisor =
      !selectedSupervisorId || s.supervisor_id === parseInt(selectedSupervisorId, 10);
    const matchJob = !selectedJobId || s.job_id === parseInt(selectedJobId, 10);
    const matchHasSupervisor =
      !hasSupervisor ||
      (hasSupervisor === "yes" && s.supervisor_id) ||
      (hasSupervisor === "no" && !s.supervisor_id);
    const prefs = getNotificationPrefs(s);
    const matchEmailSub =
      !emailSub ||
      (emailSub === "yes" && prefs.email) ||
      (emailSub === "no" && !prefs.email);
    const matchSmsSub =
      !smsSub ||
      (smsSub === "yes" && prefs.sms) ||
      (smsSub === "no" && !prefs.sms);
    const matchActiveSub =
      !activeSub ||
      (activeSub === "yes" && prefs.active) ||
      (activeSub === "no" && !prefs.active);
    const matchTargettingSub =
      !targettingSub ||
      (targettingSub === "yes" && prefs.targetting) ||
      (targettingSub === "no" && !prefs.targetting);
    const matchJalaliSub =
      !targettingJalaliSub ||
      (targettingJalaliSub === "yes" && prefs.targetting_jalali) ||
      (targettingJalaliSub === "no" && !prefs.targetting_jalali);
    const matchGregorianSub =
      !targettingGregorianSub ||
      (targettingGregorianSub === "yes" && prefs.targetting_gregorian) ||
      (targettingGregorianSub === "no" && !prefs.targetting_gregorian);
    return (
      matchSearch &&
      matchDc &&
      matchSupervisor &&
      matchJob &&
      matchHasSupervisor &&
      matchEmailSub &&
      matchSmsSub &&
      matchActiveSub &&
      matchTargettingSub &&
      matchJalaliSub &&
      matchGregorianSub
    );
  });

  const tableTotal = filteredStaff.length;
  const tableTotalPages = Math.max(1, Math.ceil(tableTotal / TABLE_PAGE_SIZE));
  const pagedStaff = filteredStaff.slice(
    (tablePage - 1) * TABLE_PAGE_SIZE,
    tablePage * TABLE_PAGE_SIZE,
  );

  const handleSearchChange = (val) => { setSearchTerm(val); setTablePage(1); };
  const handleDcChange = (val) => { setSelectedDcName(val); setTablePage(1); };
  const handleSupervisorChange = (val) => { setSelectedSupervisorId(val); setTablePage(1); };
  const handleJobChange = (val) => { setSelectedJobId(val); setTablePage(1); };
  const handleHasSupervisorChange = (val) => { setHasSupervisor(val); setTablePage(1); };
  const handleEmailSubChange = (val) => { setEmailSub(val); setTablePage(1); };
  const handleSmsSubChange = (val) => { setSmsSub(val); setTablePage(1); };
  const handleActiveSubChange = (val) => { setActiveSub(val); setTablePage(1); };
  const handleTargettingSubChange = (val) => { setTargettingSub(val); setTablePage(1); };
  const handleTargettingJalaliSubChange = (val) => { setTargettingJalaliSub(val); setTablePage(1); };
  const handleTargettingGregorianSubChange = (val) => { setTargettingGregorianSub(val); setTablePage(1); };

  const sortedSupervisorOptions = useMemo(
    () =>
      [...staff].sort((a, b) =>
        (a.full_name || "").localeCompare(b.full_name || ""),
      ),
    [staff],
  );

  const filteredSupervisorOptions = useMemo(() => {
    const q = supervisorQuery.trim().toLowerCase();
    if (!q) return sortedSupervisorOptions;
    return sortedSupervisorOptions.filter(
      (s) =>
        s.full_name?.toLowerCase().includes(q) ||
        String(s.personnel_code || "").includes(q),
    );
  }, [sortedSupervisorOptions, supervisorQuery]);

  const selectSupervisorFilter = (person) => {
    if (!person) {
      handleSupervisorChange("");
      setSupervisorQuery("");
    } else {
      handleSupervisorChange(String(person.personnel_id));
      setSupervisorQuery(`${person.full_name} (${person.personnel_code})`);
    }
    setSupervisorDropdownOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        supervisorDropdownRef.current &&
        !supervisorDropdownRef.current.contains(event.target)
      ) {
        setSupervisorDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // =========================
  // SUPERVISOR SEARCH (in modal role tab)
  // =========================

  const filteredSupervisors = staff.filter((s) => {
    if (selectedStaff && s.personnel_id === selectedStaff.personnel_id) return false;
    const q = supSearch.toLowerCase();
    const matchSearch =
      !q ||
      s.full_name?.toLowerCase().includes(q) ||
      String(s.personnel_code || "").includes(q);
    const matchDc = !supDcFilter || s.dc_name === supDcFilter;
    return matchSearch && matchDc;
  });

  const supTotalPages = Math.max(1, Math.ceil(filteredSupervisors.length / SUP_PAGE_SIZE));
  const pagedSupervisors = filteredSupervisors.slice(
    (supPage - 1) * SUP_PAGE_SIZE,
    supPage * SUP_PAGE_SIZE,
  );

  const selectedSupervisor = supervisorId ? staffMap[supervisorId] : null;

  // =========================
  // SETTINGS MODAL
  // =========================

  const handleRules = async (sm) => {
    try {
      setRuleActiveTab("role");
      const data = await getProfileById(sm.personnel_id);

      setSelectedStaff(data);
      setRole(data.job_details?.id || "");
      setSupervisorId(data.supervisor_id ? parseInt(data.supervisor_id, 10) : null);
      setFinancialClaims(data.financial_claims ?? "");
      setSelectedSaleoffices([...(data.saleoffice_ids ?? [])]);
      setSelectedTargetGroups([...(data.product_group_ids ?? [])]);
      setGroupPercents(readGroupCommissionMap(data));
      setMonthlyCommission(readMonthlyCommission(data));

      setSupSearch("");
      setSupPage(1);
      setSupDcFilter("");
    } catch (err) {
      console.error("Error loading profile:", err);
    }
  };

  const closeModal = () => {
    setSelectedStaff(null);
    setRole("");
    setSupervisorId(null);
    setSelectedSaleoffices([]);
    setSelectedTargetGroups([]);
    setGroupPercents({});
    setMonthlyCommission(readMonthlyCommission(null));
  };

  // =========================
  // SAVE
  // =========================

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!selectedStaff) return;

    const basketSum = sumNumbers(
      selectedTargetGroups.map((id) => groupPercents[id]?.basket_percent),
    );
    if (selectedTargetGroups.length > 0 && !almostEqual(basketSum, 100)) {
      alert("مجموع درصد سبد گروه‌های انتخاب‌شده باید ۱۰۰٪ باشد");
      return;
    }

    const enabledWeights = (monthlyCommission.components || []).filter((c) => c.enabled);
    const weightSum = sumNumbers(enabledWeights.map((c) => c.weight));
    if (enabledWeights.length > 0 && !almostEqual(weightSum, 100)) {
      alert("مجموع درصد سهم مؤلفه‌های فعال پورسانت باید ۱۰۰٪ باشد");
      return;
    }

    try {
      setSubmitting(true);

      const selectedJob = jobs.find((j) => String(j.id) === String(role));
      const product_group_commission = selectedTargetGroups.map((id) => ({
        id,
        basket_percent: Number(groupPercents[id]?.basket_percent) || 0,
        excess_percent: Number(groupPercents[id]?.excess_percent) || 0,
        max_payout_percent:
          groupPercents[id]?.max_payout_percent === "" ||
          groupPercents[id]?.max_payout_percent == null
            ? null
            : Number(groupPercents[id].max_payout_percent),
      }));

      await updateProfile.mutateAsync({
        id: selectedStaff.personnel_id,
        body: buildProfileUpdateBody({
          job_id: role,
          supervisor_id: supervisorId,
          level_type: selectedJob?.level_type ?? null,
          product_group_ids: selectedTargetGroups,
          financial_claims: financialClaims,
          saleoffice_ids: selectedSaleoffices,
          customization: {
            product_group_commission,
            monthly_commission: {
              approved_amount: Number(monthlyCommission.approved_amount) || 0,
              components: (monthlyCommission.components || []).map((row) => ({
                ...row,
                weight: Number(row.weight) || 0,
              })),
            },
          },
        }),
      });

      alert("ذخیره شد");
      closeModal();
      refetchStaff();
    } catch (e) {
      console.error("Save error:", e);
      alert(e.response?.data?.error || "خطا در ذخیره");
    } finally {
      setSubmitting(false);
    }
  };

  // =========================
  // TOGGLES
  // =========================

  const toggleNotification = async (sm, channel) => {
    const prefs = getNotificationPrefs(sm);
    const raw =
      sm?.customization?.notifications &&
      typeof sm.customization.notifications === "object"
        ? sm.customization.notifications
        : {};
    const nextValue = !prefs[channel];
    const next = {
      ...raw,
      [channel]: nextValue,
    };
    if (channel === "targetting" && nextValue && !prefs.targetting_jalali && !prefs.targetting_gregorian) {
      next.targetting_jalali = true;
      next.targetting_gregorian = true;
    }
    if (
      (channel === "targetting_jalali" || channel === "targetting_gregorian") &&
      nextValue
    ) {
      next.targetting = true;
    }
    try {
      await updateProfile.mutateAsync({
        id: sm.personnel_id,
        body: {
          customization: {
            notifications: next,
          },
        },
      });
    } catch (err) {
      console.error("Error updating notification prefs:", err);
      alert(err?.response?.data?.error || err.message || "خطا در ذخیره اعلان");
    }
  };

  const toggleTargetGroup = (id) => {
    setSelectedTargetGroups((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id],
    );
    setGroupPercents((prev) => {
      if (selectedTargetGroups.includes(id)) {
        const next = { ...prev };
        delete next[id];
        return next;
      }
      return { ...prev, [id]: prev[id] || emptyGroupPercents() };
    });
  };

  const toggleAllTargetGroups = () => {
    if (selectedTargetGroups.length === targetGroups.length) {
      setSelectedTargetGroups([]);
      setGroupPercents({});
    } else {
      setSelectedTargetGroups(targetGroups.map((p) => p.id));
      setGroupPercents((prev) => {
        const next = { ...prev };
        targetGroups.forEach((p) => {
          if (!next[p.id]) next[p.id] = emptyGroupPercents();
        });
        return next;
      });
    }
  };

  const onPercentChange = (id, field, value) => {
    setGroupPercents((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || emptyGroupPercents()), [field]: value },
    }));
  };

  const toggleSaleoffice = (id) => {
    const numId = Number(id);
    setSelectedSaleoffices((prev) =>
      prev.includes(numId) ? prev.filter((p) => p !== numId) : [...prev, numId],
    );
  };

  const toggleAllSaleoffices = () => {
    if (selectedSaleoffices.length === saleoffices.length) {
      setSelectedSaleoffices([]);
    } else {
      setSelectedSaleoffices(saleoffices.map((s) => Number(s.saleoffice_id)));
    }
  };

  const allSaleofficesSelected = saleoffices.length > 0 && selectedSaleoffices.length === saleoffices.length;

  const supTh =
    "px-2 py-1.5 text-right border-b border-muted font-semibold text-muted-foreground text-xs";

  const supTd =
    "px-2 py-1.5 text-right border-b border-muted text-sm";

  // =========================
  // UI
  // =========================

  return (
    <div className="w-full">
      {/* HEADER - FILTERS IN TWO ROWS */}
      <div className="mb-5 space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
        {/* ROW 1: Search and searchable supervisor dropdown */}
        <div className="flex items-end gap-3 flex-wrap">
          {/* Search input */}
          <FormField label="جستجو" htmlFor="staff-search" layout="inline" className="flex-1 min-w-[220px]">
            <SearchInput
              id="staff-search"
              placeholder="نام، کد پرسنلی یا کد ملی..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </FormField>

          {/* Searchable supervisor dropdown */}
          <FormField label="سرپرست" htmlFor="supervisor-filter" layout="inline" className="flex-1 min-w-[250px]">
            <div className="relative" ref={supervisorDropdownRef}>
              <Input
                id="supervisor-filter"
                type="text"
                variant="search"
                className="w-full"
                placeholder="نام یا کد سرپرست..."
                value={supervisorQuery}
                onChange={(e) => {
                  setSupervisorQuery(e.target.value);
                  setSupervisorDropdownOpen(true);
                  if (selectedSupervisorId) {
                    handleSupervisorChange("");
                  }
                }}
                onFocus={() => setSupervisorDropdownOpen(true)}
              />
              {supervisorDropdownOpen && (
                <ul className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-border bg-white shadow-lg">
                  <li>
                    <button
                      type="button"
                      className="w-full px-3 py-2 text-right text-sm text-slate-600 hover:bg-muted"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => selectSupervisorFilter(null)}
                    >
                      تمام سرپرستان ({fmtInt(staff.length)})
                    </button>
                  </li>
                  {filteredSupervisorOptions.length === 0 ? (
                    <li className="px-3 py-2 text-sm text-slate-400">موردی یافت نشد</li>
                  ) : (
                    filteredSupervisorOptions.map((person) => (
                      <li key={person.personnel_id}>
                        <button
                          type="button"
                          className={cn(
                            "w-full px-3 py-2 text-right text-sm hover:bg-muted",
                            String(selectedSupervisorId) === String(person.personnel_id)
                              ? "bg-indigo-50 text-indigo-700 font-medium"
                              : "text-slate-800",
                          )}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => selectSupervisorFilter(person)}
                        >
                          {person.full_name} ({localizeDigits(person.personnel_code ?? "")})
                        </button>
                      </li>
                    ))
                  )}
                </ul>
              )}
            </div>
          </FormField>
        </div>

        {/* ROW 2: DC, Job, Has Supervisor, and Clear Button */}
        <div className="flex items-end gap-3 flex-wrap">
          {/* DC Name dropdown */}
          <FormField label="مرکز توزیع" htmlFor="dc-filter" layout="inline" className="flex-1 min-w-[200px]">
            <Select
              id="dc-filter"
              variant="filter"
              className="min-w-[200px] w-full"
              value={selectedDcName}
              onChange={(e) => handleDcChange(e.target.value)}
            >
              <option value="">تمام مراکز</option>
              {dcOptions.map((dc) => (
                <option key={dc} value={dc}>{dc}</option>
              ))}
            </Select>
          </FormField>

          {/* Job Name dropdown */}
          <FormField label="شغل" htmlFor="job-filter" layout="inline" className="flex-1 min-w-[200px]">
            <Select
              id="job-filter"
              variant="filter"
              className="min-w-[200px] w-full"
              value={selectedJobId}
              onChange={(e) => handleJobChange(e.target.value)}
            >
              <option value="">تمام شغل‌ها</option>
              {jobs.map((job) => (
                <option key={job.id} value={job.id}>{job.name}</option>
              ))}
            </Select>
          </FormField>

          {/* Has Supervisor Toggle */}
          <FormField label="سرپرست دارد" layout="inline" className="flex-1 min-w-[180px]">
            <Select
              value={hasSupervisor}
              onChange={(e) => handleHasSupervisorChange(e.target.value)}
              variant="filter"
              className="w-full"
            >
              <option value="">همه</option>
              <option value="yes">✓ دارای سرپرست</option>
              <option value="no">✗ بدون سرپرست</option>
            </Select>
          </FormField>

          <FormField label="اشتراک ایمیل" layout="inline" className="flex-1 min-w-[160px]">
            <Select
              value={emailSub}
              onChange={(e) => handleEmailSubChange(e.target.value)}
              variant="filter"
              className="w-full"
            >
              <option value="">همه</option>
              <option value="yes">✓ مشترک ایمیل</option>
              <option value="no">✗ بدون ایمیل</option>
            </Select>
          </FormField>

          <FormField label="اشتراک پیامک" layout="inline" className="flex-1 min-w-[160px]">
            <Select
              value={smsSub}
              onChange={(e) => handleSmsSubChange(e.target.value)}
              variant="filter"
              className="w-full"
            >
              <option value="">همه</option>
              <option value="yes">✓ مشترک پیامک</option>
              <option value="no">✗ بدون پیامک</option>
            </Select>
          </FormField>

          <FormField label="اجازه ورود" layout="inline" className="flex-1 min-w-[160px]">
            <Select
              value={activeSub}
              onChange={(e) => handleActiveSubChange(e.target.value)}
              variant="filter"
              className="w-full"
            >
              <option value="">همه</option>
              <option value="yes">✓ مجاز به ورود</option>
              <option value="no">✗ ورود غیرفعال</option>
            </Select>
          </FormField>

          <FormField label="تارگت‌گذاری" layout="inline" className="flex-1 min-w-[160px]">
            <Select
              value={targettingSub}
              onChange={(e) => handleTargettingSubChange(e.target.value)}
              variant="filter"
              className="w-full"
            >
              <option value="">همه</option>
              <option value="yes">✓ مجاز به تارگت</option>
              <option value="no">✗ بدون تارگت‌گذاری</option>
            </Select>
          </FormField>

          <FormField label="تارگت شمسی" layout="inline" className="flex-1 min-w-[150px]">
            <Select
              value={targettingJalaliSub}
              onChange={(e) => handleTargettingJalaliSubChange(e.target.value)}
              variant="filter"
              className="w-full"
            >
              <option value="">همه</option>
              <option value="yes">✓ شمسی</option>
              <option value="no">✗ بدون شمسی</option>
            </Select>
          </FormField>

          <FormField label="تارگت میلادی" layout="inline" className="flex-1 min-w-[150px]">
            <Select
              value={targettingGregorianSub}
              onChange={(e) => handleTargettingGregorianSubChange(e.target.value)}
              variant="filter"
              className="w-full"
            >
              <option value="">همه</option>
              <option value="yes">✓ میلادی</option>
              <option value="no">✗ بدون میلادی</option>
            </Select>
          </FormField>

          {/* Clear button - moved to end */}
          {(searchTerm || selectedDcName || selectedSupervisorId || selectedJobId || hasSupervisor || emailSub || smsSub || activeSub || targettingSub || targettingJalaliSub || targettingGregorianSub || supervisorQuery) && (
            <FormField label="&nbsp;" layout="inline">
              <Button
                type="button"
                variant="ghost"
                className="px-3.5 py-2 bg-red-100 text-red-600 hover:bg-red-300 hover:text-red-600"
                onClick={() => {
                  handleSearchChange("");
                  handleDcChange("");
                  handleSupervisorChange("");
                  handleJobChange("");
                  handleHasSupervisorChange("");
                  handleEmailSubChange("");
                  handleSmsSubChange("");
                  handleActiveSubChange("");
                  handleTargettingSubChange("");
                  handleTargettingJalaliSubChange("");
                  handleTargettingGregorianSubChange("");
                  setSupervisorQuery("");
                  setSupervisorDropdownOpen(false);
                }}
              >
                پاک کردن فیلتر
              </Button>
            </FormField>
          )}
        </div>
      </div>

      {/* TABLE */}
      {loading ? (
        <LoadingBlock message="در حال بارگذاری..." />
      ) : (
        <>
          <div className="overflow-hidden rounded-card border border-border shadow-card-sm">
            <DataTableCard
              className="border-0 shadow-none rounded-none"
              columns={STAFF_COLUMNS}
              headerClassName="bg-gradient-to-br from-slate-50 to-muted"
              isEmpty={filteredStaff.length === 0}
              emptyMessage="داده‌ای یافت نشد"
            >
              {pagedStaff.map((sm, idx) => (
                <DataTableRow key={sm.personnel_code}>
                  <DataTableCell className="!text-slate-400 !text-xs w-9 text-center">
                    {fmtInt((tablePage - 1) * TABLE_PAGE_SIZE + idx + 1)}
                  </DataTableCell>

                  <DataTableCell>{localizeDigits(sm.personnel_code ?? "")}</DataTableCell>

                  <DataTableCell className="font-semibold !text-slate-800">
                    {sm.full_name}
                  </DataTableCell>

                  <DataTableCell>{localizeDigits(sm.national_code ?? "")}</DataTableCell>

                  <DataTableCell>
                    {sm.job_id ? (
                      <Badge variant="job">{getJobName(sm.job_id)}</Badge>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </DataTableCell>

                  <DataTableCell>
                    {staffMap[sm.supervisor_id]?.full_name ?? ""}
                  </DataTableCell>

                  <DataTableCell>
                    {sm.saleoffice_ids && sm.saleoffice_ids.length > 0 ? (
                      <Badge variant="default">{localizeDigits(sm.saleoffice_ids.join(", "))}</Badge>
                    ) : ""}
                  </DataTableCell>

                  <DataTableCell>
                    {sm.dc_name ? (
                      <Badge variant="dc">{sm.dc_name}</Badge>
                    ) : ""}
                  </DataTableCell>

                  <DataTableCell className="text-center">
                    <div className="flex flex-col items-center gap-0.5">
                      <NotifToggle
                        active={getNotificationPrefs(sm).email}
                        disabled={updateProfile.isPending}
                        title={
                          sm.email
                            ? getNotificationPrefs(sm).email
                              ? "غیرفعال کردن اعلان ایمیل"
                              : "فعال کردن اعلان ایمیل"
                            : "ایمیل در پروفایل ثبت نشده"
                        }
                        onToggle={() => toggleNotification(sm, "email")}
                      />
                      {!sm.email ? (
                        <span className="text-xs text-amber-600">بدون ایمیل</span>
                      ) : null}
                    </div>
                  </DataTableCell>

                  <DataTableCell className="text-center">
                    <div className="flex flex-col items-center gap-0.5">
                      <NotifToggle
                        active={getNotificationPrefs(sm).sms}
                        disabled={updateProfile.isPending}
                        title={
                          sm.mobile
                            ? getNotificationPrefs(sm).sms
                              ? "غیرفعال کردن اعلان پیامک"
                              : "فعال کردن اعلان پیامک"
                            : "موبایل در پروفایل ثبت نشده"
                        }
                        onToggle={() => toggleNotification(sm, "sms")}
                      />
                      {!sm.mobile ? (
                        <span className="text-xs text-amber-600">بدون موبایل</span>
                      ) : null}
                    </div>
                  </DataTableCell>

                  <DataTableCell className="text-center">
                    <NotifToggle
                      active={getNotificationPrefs(sm).active}
                      disabled={updateProfile.isPending}
                      title={
                        getNotificationPrefs(sm).active
                          ? "غیرفعال کردن ورود به سامانه"
                          : "فعال کردن ورود به سامانه"
                      }
                      onToggle={() => toggleNotification(sm, "active")}
                    />
                  </DataTableCell>

                  <DataTableCell className="text-center">
                    <NotifToggle
                      active={getNotificationPrefs(sm).targetting}
                      disabled={updateProfile.isPending}
                      title={
                        getNotificationPrefs(sm).targetting
                          ? "لغو اجازه تارگت‌گذاری برای پرسنل"
                          : "اجازه تارگت‌گذاری برای پرسنل"
                      }
                      onToggle={() => toggleNotification(sm, "targetting")}
                    />
                  </DataTableCell>

                  <DataTableCell className="text-center">
                    <div className="flex items-center justify-center gap-2">
                      <div className="flex flex-col items-center gap-0.5">
                        <span className="text-[10px] font-bold text-indigo-600">شمسی</span>
                        <NotifToggle
                          active={getNotificationPrefs(sm).targetting_jalali}
                          disabled={updateProfile.isPending}
                          title="اجازه تارگت گروه‌های شمسی"
                          onToggle={() => toggleNotification(sm, "targetting_jalali")}
                        />
                      </div>
                      <div className="flex flex-col items-center gap-0.5">
                        <span className="text-[10px] font-bold text-amber-700">میلادی</span>
                        <NotifToggle
                          active={getNotificationPrefs(sm).targetting_gregorian}
                          disabled={updateProfile.isPending}
                          title="اجازه تارگت گروه‌های میلادی"
                          onToggle={() => toggleNotification(sm, "targetting_gregorian")}
                        />
                      </div>
                    </div>
                  </DataTableCell>

                  <DataTableCell>
                    <LastLoginCell value={sm.last_login_at} />
                  </DataTableCell>

                  <DataTableCell>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="hover:bg-amber-50 hover:border-amber-400 hover:text-amber-800"
                      onClick={() => handleRules(sm)}
                    >
                      ⚙️ تنظیمات
                    </Button>
                  </DataTableCell>
                </DataTableRow>
              ))}
            </DataTableCard>

            <Pagination
              page={tablePage}
              pageCount={tableTotalPages}
              onChange={setTablePage}
              showPageNumbers
              summary={
                <>
                  نمایش {fmtInt(Math.min((tablePage - 1) * TABLE_PAGE_SIZE + 1, tableTotal))}–{fmtInt(Math.min(tablePage * TABLE_PAGE_SIZE, tableTotal))} از {fmtInt(tableTotal)} نفر
                </>
              }
            />
          </div>
        </>
      )}

      {/* MODAL */}
      <AppDialog
        open={!!selectedStaff}
        onOpenChange={(open) => !open && closeModal()}
        title={`⚙️ تنظیمات : ${selectedStaff?.full_name || ""}`}
        size="xl"
        showFooter={false}
      >
          <form onSubmit={handleFormSubmit} className="flex flex-col gap-4 md:flex-row">
          {/* TABS */}
          <div className="flex gap-2 rounded-2xl bg-gradient-to-b from-slate-50 to-indigo-50/40 p-2 max-md:overflow-x-auto md:w-44 md:flex-col">
            {MODAL_TABS.map((tab) => (
              <Button
                key={tab.key}
                type="button"
                variant="ghost"
                className={cn(
                  "justify-start gap-2 rounded-xl px-2.5 py-2.5 font-semibold text-slate-500 max-md:flex-none max-md:min-w-[130px] max-md:whitespace-nowrap hover:bg-white",
                  ruleActiveTab === tab.key && "bg-white text-indigo-800 shadow-[0_2px_8px_rgba(79,70,229,0.12)]",
                )}
                onClick={() => setRuleActiveTab(tab.key)}
              >
                <span>{tab.icon}</span>
                {tab.label}
              </Button>
            ))}
          </div>

          <div className="min-w-0 flex-1">
            {/* ROLE TAB */}
            {ruleActiveTab === "role" && (
              <div className="py-4 min-h-56">
                <FormField label="نقش" htmlFor="staff-role" width="staff">
                  <Select
                    id="staff-role"
                    variant="staff"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                  >
                    <option value="">انتخاب نشده</option>
                    {jobs.map((j) => (
                      <option key={j.id} value={j.id}>
                        {j.name}
                      </option>
                    ))}
                  </Select>
                </FormField>

                {/* Searchable supervisor selector */}
                <div className="mt-4">
                  <div className="text-sm font-semibold text-slate-600 mb-2">سرپرست</div>

                  {/* Current selection */}
                  <div className="flex items-center gap-2 mb-3 p-2 rounded-lg border border-border bg-slate-50 min-h-[38px]">
                    {selectedSupervisor ? (
                      <>
                        <span className="flex-1 text-sm text-slate-800 font-medium">
                          {selectedSupervisor.full_name}
                          <span className="mr-2 text-xs text-slate-400">{localizeDigits(selectedSupervisor.personnel_code ?? "")}</span>
                        </span>
                        <button
                          type="button"
                          className="text-red-600 text-xs hover:underline"
                          onClick={() => setSupervisorId(null)}
                        >
                          ✕ حذف
                        </button>
                      </>
                    ) : (
                      <span className="text-sm text-slate-400">سرپرستی انتخاب نشده</span>
                    )}
                  </div>

                  {/* Search controls */}
                  <div className="flex gap-2 mb-2 flex-wrap">
                    <SearchInput
                      className="flex-1 min-w-[140px]"
                      placeholder="جستجو با نام یا کد پرسنلی..."
                      value={supSearch}
                      onChange={(e) => { setSupSearch(e.target.value); setSupPage(1); }}
                    />
                    <Select
                      variant="filter"
                      className="flex-1 min-w-[130px] w-auto"
                      value={supDcFilter}
                      onChange={(e) => { setSupDcFilter(e.target.value); setSupPage(1); }}
                    >
                      <option value="">همه مراکز</option>
                      {dcOptions.map((dc) => (
                        <option key={dc} value={dc}>{dc}</option>
                      ))}
                    </Select>
                  </div>

                  {/* Supervisor list */}
                  <div className="border border-border rounded-lg overflow-hidden">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="bg-slate-50">
                          <th className={supTh}>کد پرسنلی</th>
                          <th className={supTh}>نام</th>
                          <th className={supTh}>مرکز</th>
                          <th className={supTh}>انتخاب</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedSupervisors.length === 0 ? (
                          <tr>
                            <td colSpan={4} className="px-3 py-4 text-center text-slate-400 text-xs">
                              نتیجه‌ای یافت نشد
                            </td>
                          </tr>
                        ) : (
                          pagedSupervisors.map((s) => {
                            const isSelected = supervisorId === s.personnel_id;
                            return (
                              <tr
                                key={s.personnel_id}
                                className={cn(
                                  "cursor-pointer hover:bg-muted transition-colors",
                                  isSelected && "bg-blue-50",
                                )}
                                onClick={() => setSupervisorId(isSelected ? null : s.personnel_id)}
                              >
                                <td className={supTd}>{localizeDigits(s.personnel_code ?? "")}</td>
                                <td className={cn(supTd, "font-medium text-slate-800")}>{s.full_name}</td>
                                <td className={supTd}>{s.dc_name || "—"}</td>
                                <td className={supTd}>
                                  <span className={cn(
                                    "inline-block w-4 h-4 rounded-full border-2 transition-colors",
                                    isSelected
                                      ? "bg-primary border-primary"
                                      : "border-slate-300 bg-white",
                                  )} />
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>

                    <Pagination
                      page={supPage}
                      pageCount={supTotalPages}
                      onChange={setSupPage}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* MONTHLY TARGET TAB */}
            {ruleActiveTab === "target" && (
              <div className="py-4 min-h-56">
                <p className="mb-4 text-sm text-slate-600">
                  هدف کل مشتری هر ماه در صفحه تارگت‌گذاری همان ماه ثبت می‌شود.
                </p>
                <FormField
                  label="مطالبات مالی"
                  htmlFor="staff-financial-claims"
                  width="staff"
                  hint="مبلغ مطالبات — مخرج درصد مطالبات در داشبورد (فروش ماه / مطالبات)"
                >
                  <Input
                    id="staff-financial-claims"
                    type="number"
                    variant="form"
                    min="0"
                    step="0.01"
                    inputMode="decimal"
                    placeholder="مبلغ مطالبات (ریال)"
                    value={financialClaims}
                    onChange={(e) => setFinancialClaims(e.target.value)}
                  />
                </FormField>
              </div>
            )}

            {/* PRODUCTS TAB */}
            {ruleActiveTab === "products" && (
              <StaffProductGroupsTab
                targetGroups={targetGroups}
                selectedIds={selectedTargetGroups}
                percents={groupPercents}
                onToggle={toggleTargetGroup}
                onToggleAll={toggleAllTargetGroups}
                onPercentChange={onPercentChange}
              />
            )}

            {/* MONTHLY COMMISSION TAB */}
            {ruleActiveTab === "commission" && (
              <StaffMonthlyCommissionTab
                value={monthlyCommission}
                onChange={setMonthlyCommission}
              />
            )}

            {/* SALEOFFICES TAB */}
            {ruleActiveTab === "saleoffices" && (
              <div className="py-4 min-h-56">
                <div className="flex items-center gap-3 mb-3">
                  <Button
                    type="button"
                    variant="ghost"
                    className={cn(
                      "px-3 py-1.5 bg-green-100 text-green-800 hover:bg-green-200",
                      allSaleofficesSelected && "bg-red-100 text-red-800 hover:bg-red-300",
                    )}
                    onClick={toggleAllSaleoffices}
                  >
                    {allSaleofficesSelected ? "✕ حذف همه" : "✓ انتخاب همه"}
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    {fmtInt(selectedSaleoffices.length)} از {fmtInt(saleoffices.length)} انتخاب شده
                  </span>
                </div>
                <div className="max-h-[280px] overflow-y-auto border border-border rounded-lg p-2">
                  {saleoffices.length === 0 ? (
                    <EmptyState
                      className="border-0 shadow-none py-4"
                      message="دفتر فروشی تعریف نشده است"
                    />
                  ) : (
                    saleoffices.map((s) => (
                      <label key={s.saleoffice_id} className="flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer hover:bg-muted">
                        <input
                          type="checkbox"
                          checked={selectedSaleoffices.includes(Number(s.saleoffice_id))}
                          onChange={() => toggleSaleoffice(s.saleoffice_id)}
                        />
                        <span>{s.saleoffice_name}</span>
                        <span className="text-xs text-slate-400">(#{s.saleoffice_id})</span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* BUTTONS */}
            <DialogFooter>
              <Button type="submit" variant="submit" disabled={submitting}>
                {submitting ? "در حال ذخیره..." : "💾 ذخیره"}
              </Button>
              <Button type="button" variant="cancel" onClick={closeModal}>
                بستن
              </Button>
            </DialogFooter>
          </div>
          </form>
      </AppDialog>
    </div>
  );
}
